// The pixel office, ported from ai-office-prototype.html (drawStatic / drawDesk / draw / tick / walk / overlay).
// 384×216 logical pixels on a <canvas>, scaled with image-rendering: pixelated. Name tags and speech bubbles are
// HTML on top of the canvas (the .ov overlay), positioned in % of the logical size, exactly like the prototype.
//
// Props: agents (from GET /ai-office/state), chatId (who the boss is talking to), onAgentClick(id), onFreeDesk().
// The parent can make people walk and talk through the ref: walkTo(id, 'desk' | 'meet' | 'door' | {coffee: 0|1} |
// {beside: deskIndex}), say(id, text), setLocal(id, patch), enterFromDoor(id). Used by office life (small talk),
// the hand-off walk, the morning brief and a new hire walking in.
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { px, drawPerson, statusOf } from '../pixel';

const W = 384, H = 216, AISLE = 112;
const DESKS = [{ x: 36, y: 94 }, { x: 118, y: 94 }, { x: 200, y: 94 }, { x: 77, y: 162 }, { x: 159, y: 162 }, { x: 241, y: 162 }];
const seat = (i) => ({ x: DESKS[i].x + 16, y: DESKS[i].y - 13 });
const MEET = [{ x: 306, y: 80 }, { x: 324, y: 78 }, { x: 342, y: 80 }, { x: 314, y: 126 }, { x: 338, y: 126 }, { x: 358, y: 100 }];
const DOOR = { x: 351, y: 28 };
const COFFEE = [{ x: 285, y: 35 }, { x: 298, y: 37 }]; // standing spots by the coffee machine (clear of the plant)
// Places to hang about (office life): look out of a window, browse the shelf, stand by a plant, chat on the rug.
const SPOTS = {
  window0: { x: 26, y: 34 }, window1: { x: 96, y: 34 }, shelf: { x: 250, y: 34 }, sign: { x: 176, y: 34 },
  plant: { x: 18, y: 50 }, hall: { x: 318, y: 52 }, rug0: { x: 300, y: 138 }, rug1: { x: 318, y: 140 }, rug2: { x: 336, y: 138 }, rug3: { x: 354, y: 136 },
};
const pct = (v, of) => `${(v / of) * 100}%`;
const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function sky() {
  const h = new Date().getHours();
  return h >= 6 && h < 17 ? ['#9CCBEA', '#6E8FA8'] : h >= 17 && h < 19 ? ['#E9A26B', '#7A5A6E'] : ['#1E2B4A', '#121A2E'];
}

function drawStatic(c) {
  px(c, '#CBD3DA', 0, 0, W, 46); px(c, '#B4BEC8', 0, 0, W, 4); px(c, '#8B6B4A', 0, 44, W, 3);
  px(c, '#C99A63', 0, 47, W, H - 47);
  for (let y = 47, r = 0; y < H; y += 7, r++) { px(c, '#B98A55', 0, y, W, 1); for (let x = (r % 2 ? 0 : 20); x < W; x += 40) px(c, '#B98A55', x, y, 1, 7); }
  const [s1, s2] = sky(), night = s1 === '#1E2B4A';
  [22, 92].forEach((x) => {
    px(c, '#7A8794', x - 2, 8, 52, 28); px(c, s1, x, 10, 48, 24);
    [[2, 9, 8], [11, 5, 12], [24, 12, 6], [31, 7, 9], [41, 10, 7]].forEach(([bx, bh, bw]) => {
      px(c, s2, x + bx, 34 - bh - 6, bw, bh + 6);
      if (night) { px(c, '#F5D76E', x + bx + 2, 34 - bh - 2, 1, 1); px(c, '#F5D76E', x + bx + 4, 34 - bh + 1, 1, 1); }
    });
    px(c, '#7A8794', x + 23, 10, 2, 24); px(c, '#E3E8EC', x - 3, 35, 54, 2);
  });
  px(c, '#2D3A4A', 152, 12, 52, 16); px(c, '#C99A63', 152, 12, 52, 1);
  // clock
  px(c, '#F4F4F4', 214, 12, 15, 15); px(c, '#58616D', 214, 12, 15, 1); px(c, '#58616D', 214, 26, 15, 1); px(c, '#58616D', 214, 12, 1, 15); px(c, '#58616D', 228, 12, 1, 15);
  const d = new Date(), hm = ((d.getHours() % 12) + d.getMinutes() / 60) / 12 * Math.PI * 2, mm = d.getMinutes() / 60 * Math.PI * 2;
  for (let i = 0; i < 5; i++) px(c, '#1A1A1A', 221 + Math.sin(hm) * i * 0.9, 19 - Math.cos(hm) * i * 0.9, 1, 1);
  for (let i = 0; i < 7; i++) px(c, '#C4384F', 221 + Math.sin(mm) * i, 19 - Math.cos(mm) * i, 1, 1);
  // shelf
  px(c, '#6B4A2E', 238, 14, 34, 32); px(c, '#4E3420', 240, 16, 30, 28); px(c, '#6B4A2E', 240, 29, 30, 2);
  ['#C4384F', '#2F6FB0', '#E3A33B', '#3E9C6E', '#7A55B5', '#D2A07A', '#2F6FB0'].forEach((col, i) => { px(c, col, 241 + i * 4, 19 + (i % 2), 3, 10 - (i % 2)); px(c, col, 242 + i * 4, 32 + (i % 3), 3, 12 - (i % 3)); });
  // cooler
  px(c, '#E3E8EC', 282, 24, 10, 20); px(c, '#8FC7E8', 283, 16, 8, 9); px(c, '#58616D', 284, 30, 2, 2);
  // door
  px(c, '#6B4A2E', 338, 10, 28, 36); px(c, '#8B6B4A', 340, 12, 24, 34); px(c, '#D9B45B', 360, 28, 2, 2);
  // rug + plants
  px(c, '#4F6D7C', 288, 74, 92, 80); px(c, '#5F7F8E', 291, 77, 86, 74);
  [[8, 52], [274, 52], [8, 192], [372, 190]].forEach(([x, y]) => { px(c, '#2F7D4A', x, y, 8, 6); px(c, '#3E9C5C', x + 1, y - 4, 6, 5); px(c, '#2F7D4A', x + 3, y - 7, 2, 4); px(c, '#A0522D', x + 1, y + 6, 6, 7); px(c, '#7E3F22', x + 1, y + 6, 6, 1); });
}

// The office cat: 9×5, tail up, legs move while walking.
function drawCat(c, x, y, frame, walking) {
  x = Math.round(x); y = Math.round(y);
  const s = walking ? frame % 2 : 0;
  px(c, '#E3A33B', x + 1, y + 1, 6, 3); px(c, '#E3A33B', x + 6, y - 1, 3, 3); px(c, '#C9822B', x + 6, y - 2, 1, 1); px(c, '#C9822B', x + 8, y - 2, 1, 1);
  px(c, '#15181E', x + 7, y, 1, 1); px(c, '#E3A33B', x, y - 1 - s, 1, 2);
  px(c, '#C9822B', x + 1, y + 4, 1, 1 + s); px(c, '#C9822B', x + 5, y + 4, 1, 2 - s);
}

function drawDesk(c, i, occupant, status, frame, free) {
  const d = DESKS[i];
  px(c, '#A86F45', d.x, d.y, 40, 3); px(c, '#8A5A35', d.x, d.y + 3, 40, 9); px(c, '#74492A', d.x, d.y + 11, 40, 1);
  px(c, '#5E3D24', d.x + 2, d.y + 12, 2, 3); px(c, '#5E3D24', d.x + 36, d.y + 12, 2, 3);
  if (free && !occupant) { px(c, '#F4F4F4', d.x + 15, d.y - 5, 10, 6); px(c, '#C4384F', d.x + 16, d.y - 4, 8, 1); return; }
  const st = occupant ? status : 'idle';
  px(c, '#2A2F38', d.x + 3, d.y - 9, 13, 9); px(c, '#2A2F38', d.x + 8, d.y - 1, 3, 2);
  px(c, statusOf(st).c, d.x + 4, d.y - 2, 1, 1);
  if (st === 'working' && frame % 2) px(c, 'rgba(255,220,140,.35)', d.x + 2, d.y - 10, 15, 1);
  px(c, '#F2F2F2', d.x + 29, d.y, 7, 2); px(c, '#E8E8E8', d.x + 35, d.y - 3, 3, 3);
}

const OfficeCanvas = forwardRef(function OfficeCanvas({ agents, chatId, onAgentClick, onFreeDesk }, ref) {
  const cvRef = useRef(null);
  const ovRef = useRef(null);
  // Mutable world, never in React state: positions change every frame.
  const world = useRef({ pos: {}, frame: 0, npcs: {}, fx: {} });
  const live = useRef({ agents: [], chatId: null, onAgentClick, onFreeDesk });
  live.current.onAgentClick = onAgentClick;
  live.current.onFreeDesk = onFreeDesk;
  live.current.chatId = chatId;

  const seated = (a) => a.desk != null && a.desk >= 0 && a.desk < DESKS.length;
  // The lowest empty desk: the one a new hire gets (same rule as the server), or null when all are taken.
  const freeDesk = () => {
    const used = new Set(live.current.agents.map((a) => a.desk));
    const i = DESKS.findIndex((_, k) => !used.has(k));
    return i < 0 ? null : i;
  };

  // Keep positions in step with the server's list: new people appear at their seat (or at the door when they
  // have no desk yet); status/bubble/progress come from the server unless the person is walking.
  useEffect(() => {
    const list = (agents || []).filter(seated);
    live.current.agents = list;
    const pos = world.current.pos;
    for (const a of list) {
      if (!pos[a.id]) {
        const s = seat(a.desk);
        pos[a.id] = { x: s.x, y: s.y, seated: true, path: [], status: 'idle', bub: null, prog: null };
      }
      const p = pos[a.id];
      p.status = a.status || 'idle';
      p.bub = a.bubble || null;
      p.prog = a.progress == null ? null : Number(a.progress);
    }
    for (const id of Object.keys(pos)) if (!list.find((a) => a.id === id)) delete pos[id];
    // Rebuild the tags only when who sits where changes, not on every status poll.
    const sig = list.map((a) => [a.id, a.desk, a.name, a.role].join('|')).join(';') + `#${freeDesk()}`;
    if (sig !== world.current.sig) { world.current.sig = sig; buildOverlay(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents]);

  function buildOverlay() {
    const ov = ovRef.current;
    if (!ov) return;
    ov.innerHTML = `<div class="sign" style="left:${pct(178, W)};top:${pct(20, H)}">HEAD OFFICE</div>`;
    for (const a of live.current.agents) {
      const s = seat(a.desk), d = DESKS[a.desk];
      const t = document.createElement('button');
      t.className = 'tag'; t.dataset.id = a.id; t.type = 'button';
      t.style.left = pct(s.x + 4, W); t.style.top = pct(d.y + 16, H);
      t.innerHTML = `<i class="dot"></i><span class="n">${esc(a.name)}</span><span class="r">· ${esc(a.role)}</span>`;
      t.dataset.full = `${a.name} · ${a.role}`;
      t.setAttribute('aria-label', `Talk to ${a.name}, ${a.role}`);
      t.onclick = () => live.current.onAgentClick && live.current.onAgentClick(a.id);
      ov.appendChild(t);
      const b = document.createElement('div');
      b.className = 'bub'; b.dataset.bub = a.id; b.hidden = true;
      ov.appendChild(b);
    }
    const fd = freeDesk();
    if (fd != null) {
      const d = DESKS[fd], t = document.createElement('button');
      t.className = 'tag free'; t.type = 'button';
      t.style.left = pct(d.x + 20, W); t.style.top = pct(d.y + 16, H);
      t.textContent = 'Free desk · Hire';
      t.onclick = () => live.current.onFreeDesk && live.current.onFreeDesk();
      ov.appendChild(t);
    }
  }

  // Tag dots and bubbles, refreshed every frame (cheap: only touches the DOM when something changed).
  function placeOverlay() {
    const ov = ovRef.current;
    if (!ov) return;
    const { pos, frame, npcs } = world.current;
    const ow = ov.clientWidth;
    for (const [id, n] of Object.entries(npcs)) {
      let b = ov.querySelector(`.bub[data-npc="${id}"]`);
      if (!b) { b = document.createElement('div'); b.className = 'bub say'; b.dataset.npc = id; ov.appendChild(b); }
      if (!n.say) { b.hidden = true; continue; }
      if (b._h !== n.say) { b.textContent = n.say; b._h = n.say; b._w = b.offsetWidth; }
      b.hidden = false;
      const x = ((n.x + 4) / W) * ow, half = (b._w || 60) / 2 + 4;
      b.style.left = `${Math.min(Math.max(x, half), ow - half)}px`;
      b.style.top = pct(n.y - (n.kind === 'cat' ? 6 : 3), H);
    }
    ov.querySelectorAll('.bub[data-npc]').forEach((b) => { if (!npcs[b.dataset.npc]) b.remove(); });
    for (const a of live.current.agents) {
      const p = pos[a.id];
      if (!p) continue;
      const tag = ov.querySelector(`.tag[data-id="${a.id}"]`);
      if (tag) {
        const st = p.seated || p.status !== 'idle' ? p.status : 'away';
        const col = statusOf(st).c, sel = live.current.chatId === a.id;
        if (tag._c !== col) { tag.querySelector('.dot').style.background = col; tag.title = `${tag.dataset.full} (${statusOf(st).t})`; tag._c = col; }
        if (tag._s !== sel) { tag.classList.toggle('sel', sel); tag._s = sel; }
      }
      const b = ov.querySelector(`.bub[data-bub="${a.id}"]`);
      if (!b) continue;
      let html = null, say = false;
      if (p.prog != null) html = `Working ${Math.round(p.prog * 100)}%<span class="bar"><b style="width:${p.prog * 100}%"></b></span>`;
      else if (p.bub) html = esc(p.bub);
      else if (p.say) { html = esc(p.say); say = true; }
      else if (p.status === 'working') html = '•'.repeat((frame % 3) + 1);
      else if (p.status === 'waiting') html = 'Needs you';
      if (html == null) { if (!b.hidden) b.hidden = true; continue; }
      if (b._h !== html) { b.innerHTML = html; b._h = html; }
      b.hidden = false;
      b.classList.toggle('is-alert', p.status === 'waiting' || p.status === 'error');
      b.classList.toggle('say', say);
      // Keep the bubble inside the office (it is centred on the person, so near an edge it would be cut off).
      const key = `${b._h}|${say}|${ow}`;
      if (b._k !== key) { b._w = b.offsetWidth; b._k = key; }
      const x = ((p.x + 4) / W) * ow, half = b._w / 2 + 4;
      b.style.left = `${ow > half * 2 ? Math.min(Math.max(x, half), ow - half) : x}px`;
      b.style.top = pct(p.y - 3, H);
    }
  }

  function draw(c) {
    const { pos, frame } = world.current;
    const agentsNow = live.current.agents;
    c.clearRect(0, 0, W, H);
    drawStatic(c);
    const fx = world.current.fx;
    if (fx.rain) {
      // Drops falling past both windows.
      [22, 92].forEach((wx) => { for (let k = 0; k < 9; k++) { const dx = (k * 11 + frame * 3) % 46, dy = (k * 7 + frame * 5) % 22; px(c, 'rgba(210,230,255,.85)', wx + 1 + dx, 10 + dy, 1, 2); } });
    }
    const items = [];
    const fd = freeDesk();
    DESKS.forEach((d, i) => {
      const s = seat(i);
      const occ = agentsNow.find((a) => a.desk === i);
      items.push({ y: d.y - 2, f: () => px(c, '#3B4350', s.x - 3, s.y + 5, 14, 10) });
      items.push({ y: d.y, f: () => drawDesk(c, i, occ, occ && pos[occ.id] ? pos[occ.id].status : 'idle', frame, i === fd) });
    });
    items.push({ y: 100, f: () => {
      px(c, '#5E3D24', 304, 103, 58, 25); px(c, '#94673F', 302, 100, 62, 22); px(c, '#7B5536', 302, 120, 62, 3); px(c, '#F2F2F2', 318, 106, 8, 5); px(c, '#F2F2F2', 336, 108, 6, 4);
      if (fx.cake) {
        // Birthday cake with candles (flickering flames).
        px(c, '#F6E3C8', 326, 100, 14, 6); px(c, '#E5566F', 326, 102, 14, 1); px(c, '#8A5A35', 326, 105, 14, 1);
        [329, 333, 337].forEach((cx, i) => { px(c, '#7FB3E0', cx, 97, 1, 3); px(c, (frame + i) % 2 ? '#FFD34D' : '#FF9F2E', cx, 95, 1, 2); });
      }
    } });
    agentsNow.forEach((a, k) => {
      const p = pos[a.id];
      if (!p) return;
      const o = { frame: frame + k, typing: p.status === 'working', talk: p.status === 'talking' || !!p.say, blink: (frame + k * 7) % 23 === 0, mug: !!p.mug };
      // A little hop while dancing or laughing (office life).
      const hop = p.bounce && !p.path.length && (frame + k) % 2 ? 1 : 0;
      if (p.seated) { const d = DESKS[a.desk]; items.push({ y: d.y - 1, f: () => drawPerson(c, p.x, p.y - hop, a.look, { ...o, typing: o.typing || !!p.fakeWork }) }); }
      else items.push({ y: p.y + 18, f: () => drawPerson(c, p.x, p.y - hop, a.look, { ...o, stand: true, walking: p.path.length > 0, typing: false }) });
    });
    // Visitors: the cha-wala (a person) and the office cat.
    for (const n of Object.values(world.current.npcs)) {
      if (n.kind === 'cat') items.push({ y: n.y + 5, f: () => drawCat(c, n.x, n.y, frame, n.path.length > 0) });
      else items.push({ y: n.y + 18, f: () => drawPerson(c, n.x, n.y, n.look, { frame, stand: true, walking: n.path.length > 0, mug: true, talk: !!n.say }) });
    }
    items.sort((a, b) => a.y - b.y).forEach((i) => i.f());
    if (fx.dark) {
      // Load-shedding: the room goes dark; phone torches glow where people hold them.
      c.fillStyle = 'rgba(8,12,28,.72)'; c.fillRect(0, 0, W, H);
      for (const a of agentsNow) {
        const p = pos[a.id];
        if (!p || !p.torch) continue;
        c.fillStyle = 'rgba(255,240,170,.18)'; c.fillRect(p.x - 6, p.y - 4, 20, 26);
        c.fillStyle = 'rgba(255,240,170,.22)'; c.fillRect(p.x - 2, p.y, 12, 18);
        px(c, '#FFF6C8', p.x + 8, p.y + 9, 2, 2);
      }
    }
    // little marker over whoever the boss is talking to
    const sel = live.current.chatId && pos[live.current.chatId];
    if (sel) { px(c, '#FFFFFF', sel.x + 2, sel.y - 6, 4, 1); px(c, '#FFFFFF', sel.x + 3, sel.y - 5, 2, 1); }
  }

  useEffect(() => {
    const cv = cvRef.current;
    const c = cv.getContext('2d');
    const reduce = reduceMotion();
    const w = world.current;
    let raf = 0, last = 0, acc = 0;
    const tick = (ts) => {
      const dt = Math.min(0.05, (ts - last) / 1000 || 0);
      last = ts; acc += dt;
      if (acc > 0.14) { acc = 0; world.current.frame++; }
      for (const a of live.current.agents) {
        const p = world.current.pos[a.id];
        if (!p || !p.path.length) continue;
        const [tx, ty] = p.path[0], dx = tx - p.x, dy = ty - p.y, dist = Math.hypot(dx, dy), step = (reduce ? 400 : 52 * (p.speed || 1)) * dt;
        if (dist <= step) {
          p.x = tx; p.y = ty; p.path.shift();
          if (!p.path.length && p.arrive) { const f = p.arrive; p.arrive = null; f(); }
        } else { p.x += (dx / dist) * step; p.y += (dy / dist) * step; }
      }
      for (const n of Object.values(world.current.npcs)) {
        if (!n.path.length) continue;
        const [tx, ty] = n.path[0], dx = tx - n.x, dy = ty - n.y, dist = Math.hypot(dx, dy), step = (reduce ? 400 : (n.kind === 'cat' ? 70 : 48)) * dt;
        if (dist <= step) { n.x = tx; n.y = ty; n.path.shift(); if (!n.path.length && n.arrive) { const f = n.arrive; n.arrive = null; f(); } }
        else { n.x += (dx / dist) * step; n.y += (dy / dist) * step; }
      }
      draw(c);
      placeOverlay();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    // Leaving the office screen: finish every walk still under way, so nobody waiting for one waits for ever.
    return () => {
      cancelAnimationFrame(raf);
      for (const p of Object.values(w.pos)) {
        p.arrive = null;
        if (p.cancelWalk) { const c = p.cancelWalk; p.cancelWalk = null; c(); }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onCanvasClick(e) {
    const cv = cvRef.current, r = cv.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * W, y = ((e.clientY - r.top) / r.height) * H;
    // On a touch screen the box around each person is wider, and the nearest person wins.
    const pad = e.pointerType === 'touch' || (window.matchMedia && window.matchMedia('(pointer:coarse)').matches) ? 8 : 0;
    let hit = null, best = Infinity;
    for (const a of live.current.agents) {
      const p = world.current.pos[a.id];
      if (!p || x < p.x - 3 - pad || x > p.x + 11 + pad || y < p.y - 3 - pad || y > p.y + 20 + pad) continue;
      const dist = Math.hypot(x - (p.x + 4), y - (p.y + 9));
      if (dist < best) { best = dist; hit = a; }
    }
    if (hit) return onAgentClick && onAgentClick(hit.id);
    const fd = freeDesk(), d = fd == null ? null : DESKS[fd];
    if (d && x >= d.x && x <= d.x + 40 && y >= d.y - 14 && y <= d.y + 14 && onFreeDesk) onFreeDesk();
  }

  // ---- walking (hand-off, morning brief, a new hire walking in) ----
  function leavePath(a, p) {
    if (p.seated) { const d = DESKS[a.desk]; return [[d.x - 6, p.y], [d.x - 6, AISLE]]; }
    if (p.x > 284) return [[286, p.y], [286, AISLE]];
    return p.y === AISLE ? [] : [[p.x, AISLE]];
  }
  const toSeat = (i) => { const d = DESKS[i], s = seat(i); return [[d.x - 6, AISLE], [d.x - 6, s.y], [s.x, s.y]]; };
  const toMeet = (m) => [[286, AISLE], [286, m.y], [m.x, m.y]];
  const toBeside = (i) => { const d = DESKS[i]; return [[d.x + 44, AISLE], [d.x + 44, d.y - 14]]; };

  // A new walk replaces the current one; the replaced walk's promise still resolves (so nobody waits forever).
  function startWalk(p, path, sitAfter, speed = 1) {
    if (p.cancelWalk) { const c = p.cancelWalk; p.cancelWalk = null; c(); }
    p.path = path;
    p.seated = false;
    p.speed = speed;
    return new Promise((resolve) => {
      p.cancelWalk = resolve;
      p.arrive = () => { p.cancelWalk = null; if (sitAfter) p.seated = true; resolve(); };
    });
  }

  useImperativeHandle(ref, () => ({
    // where: 'desk' | 'meet' | { beside: deskIndex } | 'door' | { coffee: 0|1 } | { spot: name in SPOTS }.
    // opts.speed: 1 walks, 2.6 runs (caught by the boss). Resolves when they arrive.
    walkTo(id, where, opts = {}) {
      const a = live.current.agents.find((x) => x.id === id), p = world.current.pos[id];
      if (!a || !p) return Promise.resolve();
      let pts, sitAfter = false;
      if (where === 'desk') { pts = toSeat(a.desk); sitAfter = true; }
      else if (where === 'meet') pts = toMeet(MEET[live.current.agents.indexOf(a) % MEET.length]);
      else if (where === 'door') pts = [[DOOR.x, AISLE], [DOOR.x, DOOR.y]];
      else if (where && where.coffee != null) { const spot = COFFEE[where.coffee % COFFEE.length]; pts = [[spot.x, AISLE], [spot.x, spot.y]]; }
      else if (where && where.beside != null) pts = toBeside(where.beside);
      else if (where && SPOTS[where.spot]) {
        const t = SPOTS[where.spot];
        pts = t.x > 284 ? [[286, AISLE], [286, t.y], [t.x, t.y]] : [[t.x, AISLE], [t.x, t.y]];
      } else return Promise.resolve();
      const path = [...leavePath(a, p), ...pts];
      return startWalk(p, path, sitAfter, opts.speed || 1);
    },
    // A newly hired person starts at the door and walks to their desk.
    enterFromDoor(id) {
      const a = live.current.agents.find((x) => x.id === id), p = world.current.pos[id];
      if (!a || !p) return Promise.resolve();
      Object.assign(p, { x: DOOR.x, y: DOOR.y });
      return startWalk(p, [[DOOR.x, AISLE], ...toSeat(a.desk)], true);
    },
    setLocal(id, patch) { const p = world.current.pos[id]; if (p) Object.assign(p, patch); },
    say(id, text) { const p = world.current.pos[id]; if (p) p.say = text || null; },
    isSeated(id) { const p = world.current.pos[id]; return !p || (p.seated && !p.path.length); },
    spots: Object.keys(SPOTS),
    // Office game effects: { dark, rain, cake } (any subset).
    setEffect(patch) { Object.assign(world.current.fx, patch); },
    // Visitors who are not employees: kind 'person' (the cha-wala) or 'cat'. They come in at the door.
    addVisitor(id, kind, look) { world.current.npcs[id] = { kind, look, x: DOOR.x, y: kind === 'cat' ? DOOR.y + 14 : DOOR.y, path: [], say: null }; },
    removeVisitor(id) { delete world.current.npcs[id]; },
    sayVisitor(id, text) { const n = world.current.npcs[id]; if (n) n.say = text || null; },
    // where: 'door' | 'meet' | { desk: i } (on the desk, for the cat) | { beside: i }. Resolves on arrival.
    walkVisitor(id, where) {
      const n = world.current.npcs[id];
      if (!n) return Promise.resolve();
      const cat = n.kind === 'cat';
      let pts;
      if (where === 'door') pts = [[n.x > 284 ? 286 : n.x, AISLE], [DOOR.x, AISLE], [DOOR.x, cat ? DOOR.y + 14 : DOOR.y]];
      else if (where === 'meet') pts = [[n.x, AISLE], [286, AISLE], [286, 100], [cat ? 330 : 320, cat ? 124 : 82]];
      else if (where && where.desk != null) { const d = DESKS[where.desk]; pts = [[n.x, AISLE], [d.x + 28, AISLE], [d.x + 28, d.y - 4]]; }
      else if (where && where.beside != null) pts = toBeside(where.beside);
      else return Promise.resolve();
      if (n.cancel) { const c = n.cancel; n.cancel = null; c(); }
      n.path = pts;
      return new Promise((resolve) => { n.cancel = resolve; n.arrive = () => { n.cancel = null; resolve(); }; });
    },
    deskOf(id) { const a = live.current.agents.find((x) => x.id === id); return a ? a.desk : null; },
  }));

  return (
    <div className="office">
      <canvas ref={cvRef} width={W} height={H} onClick={onCanvasClick} aria-label="Pixel office with AI employees at their desks" />
      <div className="ov" ref={ovRef} />
    </div>
  );
});

export default OfficeCanvas;
